import { styled, useTheme } from '@vebox/etui-system';
import { useState, useEffect, useCallback, createElement } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from './_rollupPluginBabelHelpers-3b7bd988.js';
import { Ul, Div } from '@vebox/etui-system-elements';
import { g as getNestedTreeIds, I as InsertPosition, i as itemContainsTarget, r as removeTreeItem, a as addTreeItem } from './index-5100f354.js';
import { e as TreeViewItem } from './TreeViewItem-53810024.js';

const _excluded = ["theme"];
const TreeViewUl = styled(Ul).attrs(_ref => {
  let props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    m: 0,
    p: 0,
    listStyle: ''
  }, props);
})`
  list-style-type: none;
`;

const getPlaceholderElement = theme => {
  const canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
  if (canUseDOM) {
    const placeholder = document.createElement('div');
    placeholder.setAttribute('id', 'placeholder');
    placeholder.style.height = '1px';
    placeholder.style.background = theme.palette.secondary.main;
    placeholder.style.width = '100%';
    placeholder.classList.add('placeholder');
    return placeholder;
  }
  return null;
};
const TreeView = ({
  data,
  onDataChange,
  showCheckBox,
  defaultCheckedItems,
  allowDragAndDrop,
  onDragDrop,
  checkedItems,
  onCheckedItemsChange,
  openSections,
  getChildrenToCheck = getNestedTreeIds,
  onOpenSectionChange,
  rowTemplate,
  selectedRow,
  onRowSelect,
  emptyPlaceholder,
  itemAs,
  styles = {},
  disableLabelSelection = false
}) => {
  let draggableItem = null;
  let newDraggableData = null;
  let insertItemPosition = null;
  let placeholderNode = null;
  const theme = useTheme();
  const [stateSelectedRow, setStateSelectedRow] = useState(selectedRow);
  const [stateOpenSections, setStateOpenSections] = useState(data && data.length > 0 ? [data[0].id] : []);
  const [stateCheckedItems, setCheckedItems] = useState(defaultCheckedItems && defaultCheckedItems.length ? defaultCheckedItems : []);
  const treeCheckedItems = checkedItems || stateCheckedItems;
  const treeOpenSections = openSections || stateOpenSections;
  useEffect(() => {
    setStateSelectedRow(selectedRow);
  }, [selectedRow]);
  const isRowSelected = item => (stateSelectedRow === null || stateSelectedRow === void 0 ? void 0 : stateSelectedRow.id) === item.id;
  const toggleNode = useCallback((item, isOpen) => {
    const updatedOpenSections = isOpen ? [...treeOpenSections, item.id] : treeOpenSections.filter(id => id !== item.id);
    if (treeOpenSections !== undefined && onOpenSectionChange !== undefined) {
      onOpenSectionChange(updatedOpenSections);
    } else {
      setStateOpenSections(updatedOpenSections);
    }
  }, [onOpenSectionChange, setStateOpenSections, treeOpenSections]);
  const handleCheckboxClick = (item, isChecked) => {
    let newCheckedItems = [];
    const allNestedIds = getChildrenToCheck(item).filter(id => id !== item.id);
    if (isChecked) {
      newCheckedItems = [...treeCheckedItems, ...allNestedIds, item.id];
    } else {
      newCheckedItems = treeCheckedItems.filter(selectedId => ![...allNestedIds, item.id].includes(selectedId));
    }
    newCheckedItems = Array.from(new Set(newCheckedItems));
    if (checkedItems !== undefined && onCheckedItemsChange) {
      onCheckedItemsChange(newCheckedItems);
    } else {
      setCheckedItems(newCheckedItems);
    }
  };
  const handleDragStart = (e, item) => {
    e.stopPropagation();
    draggableItem = _objectSpread2({}, item);
  };
  const handleDragOver = e => {
    e.preventDefault();
    const eventTarget = e.target;
    placeholderNode = placeholderNode || getPlaceholderElement(theme);
    const rect = eventTarget.getBoundingClientRect();
    const elementHeight = rect.bottom - rect.top;
    const mouseYPositionOverElement = e.clientY - rect.top;
    const elementCenter = elementHeight / 2;
    const insertBefore = mouseYPositionOverElement < elementCenter - 5;
    const insertAfter = mouseYPositionOverElement > elementCenter + 5;
    if (insertBefore) {
      if (placeholderNode) {
        eventTarget.parentNode.parentNode.insertBefore(placeholderNode, eventTarget.parentNode);
      }
      insertItemPosition = InsertPosition.BEFORE;
    }
    if (insertAfter) {
      if (placeholderNode) {
        eventTarget.parentNode.parentNode.insertBefore(placeholderNode, eventTarget.parentNode.nextSibling);
      }
      insertItemPosition = InsertPosition.AFTER;
    }
  };
  const handleDrop = (e, item) => {
    if (!allowDragAndDrop) return;
    e.stopPropagation();
    if (item.id !== draggableItem.id && !itemContainsTarget(item, draggableItem)) {
      const updatedData = removeTreeItem(data, draggableItem);
      newDraggableData = addTreeItem(updatedData, item, draggableItem, insertItemPosition);
    }
  };
  const handleDragEnd = e => {
    if (!allowDragAndDrop) return;
    e.preventDefault();
    e.stopPropagation();
    const parent = placeholderNode.parentNode;
    if (parent) {
      parent.removeChild(placeholderNode);
    }
    if (newDraggableData) {
      onDataChange(newDraggableData);
    }
    if (onDragDrop) {
      onDragDrop();
    }
    insertItemPosition = null;
    draggableItem = null;
    placeholderNode = null;
  };
  const handleItemClick = item => {
    onRowSelect === null || onRowSelect === void 0 ? void 0 : onRowSelect(item);
    if (!selectedRow) {
      setStateSelectedRow(item);
    }
  };
  const isDataEmpty = !data || data && data.length === 0;
  return /*#__PURE__*/createElement(TreeViewUl, _extends({
    "data-component": "TreeView",
    onDragOver: allowDragAndDrop ? handleDragOver : null
  }, styles.root), isDataEmpty && (emptyPlaceholder || /*#__PURE__*/createElement(Div, null, "No Data")), !isDataEmpty && data.map((item, index) => /*#__PURE__*/createElement(TreeViewItem, {
    as: itemAs,
    allowDragAndDrop: allowDragAndDrop,
    showCheckBox: showCheckBox,
    openSections: treeOpenSections,
    checkedItems: treeCheckedItems,
    isOpen: treeOpenSections.includes(item.id),
    isRowSelected: isRowSelected,
    key: item.id + item.name + index,
    item: item,
    onToggle: toggleNode,
    onCheckboxClick: handleCheckboxClick,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDrop: handleDrop,
    rowTemplate: rowTemplate,
    onItemClick: handleItemClick,
    styles: styles.row,
    disableLabelSelection: disableLabelSelection
  })));
};

export { TreeView as T, TreeViewUl as a };
